import { useRef, useState } from "react";
import { CheckCircle2, FileText, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { api, errMsg } from "@/lib/api";

const ROLES = ["Head Chef", "Sous Chef", "Chef de Partie", "Commis Chef", "Cook", "Pastry Chef", "Kitchen Porter"];
const PERKS = [
  { t: "Choose your shifts", d: "Pick up work that fits around your life." },
  { t: "Varied kitchens", d: "Care homes, schools and corporate sites." },
  { t: "Simple onboarding", d: "Apply once, we handle the rest." },
  { t: "Room to grow", d: "Agency work that can lead to permanent roles." },
];
const EMPTY = { full_name: "", email: "", phone: "", role: "Chef de Partie", location: "", experience_years: "", availability: "Flexible", message: "" };

const Dropzone = ({ file, setFile }) => {
  const input = useRef(null);
  const [drag, setDrag] = useState(false);
  const pick = (f) => {
    if (!f) return;
    if (!/\.(pdf|docx?|DOCX?|PDF)$/.test(f.name)) return toast.error("Please upload a PDF, DOC or DOCX file.");
    if (f.size > 8 * 1024 * 1024) return toast.error("CV must be under 8MB.");
    setFile(f);
  };
  return (
    <button
      type="button"
      onClick={() => input.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
      className={`flex w-full flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${drag ? "border-[#C59B27] bg-[#C59B27]/10" : "border-stone-300 bg-white hover:border-[#C59B27]"}`}
      data-testid="cv-dropzone"
    >
      <input ref={input} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="cv-file-input" />
      {file ? <FileText className="h-8 w-8 text-[#C59B27]" /> : <UploadCloud className="h-8 w-8 text-stone-500" />}
      <span className="font-semibold" data-testid="cv-file-name">{file ? file.name : "Drop your CV here or click to browse"}</span>
      <span className="text-xs text-stone-600">PDF, DOC or DOCX · max 8MB</span>
    </button>
  );
};

export default function Chefs() {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please attach your CV.");
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => v !== "" && fd.append(k, v));
    fd.append("cv", file);
    try {
      await api.post("/chefs/apply", fd);
      setDone(true);
      setForm(EMPTY);
      document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" });
      setFile(null);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="chefs-page">
      <section className="relative overflow-hidden bg-[#141412] pb-24 pt-36 text-[#FAF8F5] sm:pt-44 grain">
        <img src="/images/chef_team_service.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#141412] via-[#141412]/80 to-transparent" />
        <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
          <p className="eyebrow text-[#C59B27]">Culifect Chef Agency</p>
          <MaskedLines onMount delay={0.1} lines={["Cook with us.", "On your terms."]} italic={1} className="mt-6 font-display text-6xl font-bold leading-[0.95] tracking-[-0.03em] sm:text-7xl lg:text-[7rem]" testId="chefs-heading" />
          <Reveal delay={0.5}>
            <p className="mt-8 max-w-xl text-lg text-stone-300">We recruit agency chefs to staff our catering projects across the UK. Send your details and CV, and our team will be in touch.</p>
            <a href="#apply" className="btn-gold mt-10" data-testid="chefs-apply-anchor">Apply now</a>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#FAF8F5] py-20">
        <div className="mx-auto grid max-w-[1400px] gap-8 px-5 sm:grid-cols-2 sm:px-8 lg:grid-cols-4">
          {PERKS.map((p, i) => (
            <Reveal key={p.t} delay={i * 0.08} className="border-t border-stone-300 pt-6">
              <span className="font-display text-3xl italic text-[#C59B27]">0{i + 1}</span>
              <h3 className="mt-3 font-display text-2xl font-semibold">{p.t}</h3>
              <p className="mt-2 text-stone-700">{p.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="apply" className="bg-[#EFEAE1] py-24 sm:py-32" data-testid="chef-apply-section">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-5 sm:px-8 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="eyebrow text-[#9a7614]">Application</p>
            <MaskedLines lines={["Two minutes.", "One CV."]} italic={1} className="mt-4 font-display text-4xl font-bold leading-[1.02] sm:text-5xl" />
            <p className="mt-6 text-stone-700">Tell us about your experience and availability. All roles from kitchen porter to head chef are welcome.</p>
          </div>
          <div className="lg:col-span-8">
            {done ? (
              <div className="rounded-[28px] bg-white p-10 text-center" data-testid="chef-apply-success">
                <CheckCircle2 className="mx-auto h-12 w-12 text-[#3E4A3D]" />
                <h3 className="mt-4 font-display text-3xl">Application received</h3>
                <p className="mt-2 text-stone-700">Thank you. Our recruitment team will review your CV and contact you soon.</p>
                <button onClick={() => setDone(false)} className="btn-ink mt-8" data-testid="chef-apply-again-button">Submit another</button>
              </div>
            ) : (
              <form onSubmit={submit} className="grid gap-5 rounded-[28px] bg-[#FAF8F5] p-6 sm:grid-cols-2 sm:p-10" data-testid="chef-apply-form">
                <div><label className="label">Full name</label><input required className="field" value={form.full_name} onChange={set("full_name")} data-testid="chef-name-input" /></div>
                <div><label className="label">Email</label><input required type="email" className="field" value={form.email} onChange={set("email")} data-testid="chef-email-input" /></div>
                <div><label className="label">Phone</label><input required className="field" value={form.phone} onChange={set("phone")} data-testid="chef-phone-input" /></div>
                <div><label className="label">Town / city</label><input required className="field" value={form.location} onChange={set("location")} data-testid="chef-location-input" /></div>
                <div>
                  <label className="label">Role</label>
                  <select className="field" value={form.role} onChange={set("role")} data-testid="chef-role-select">
                    {ROLES.map((r) => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label">Years exp.</label><input required type="number" min="0" max="60" className="field" value={form.experience_years} onChange={set("experience_years")} data-testid="chef-experience-input" /></div>
                  <div>
                    <label className="label">Availability</label>
                    <select className="field" value={form.availability} onChange={set("availability")} data-testid="chef-availability-select">
                      {["Flexible", "Weekdays", "Weekends", "Full-time", "Part-time"].map((a) => <option key={a}>{a}</option>)}
                    </select>
                  </div>
                </div>
                <div className="sm:col-span-2"><label className="label">About you (optional)</label><textarea rows={3} className="field" value={form.message} onChange={set("message")} data-testid="chef-message-input" /></div>
                <div className="sm:col-span-2"><Dropzone file={file} setFile={setFile} /></div>
                <button disabled={busy} className="btn-ink justify-center sm:col-span-2" data-testid="chef-submit-button">{busy ? "Uploading…" : "Submit application"}</button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
